/**
 * Calendar & Task Scheduler · v2.7.65+E103
 * ════════════════════════════════════════════════════════════════
 * Agenda-view calendar for shifts, inspections, meetings, tasks, and
 * deadlines. Level 0 can schedule for any driver/staff or org-wide;
 * Level 1 can only schedule for themselves.
 *
 * v2.7.65+E103 · Recurring events. NewEventModal now exposes a
 * "Repeat this event" checkbox with daily/weekly/monthly frequency,
 * interval, and occurrence count. Series occurrences share a
 * recurrence_group_id and can be deleted individually or in bulk
 * via ?scope=series.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, CalendarDays, Plus, Trash2, RefreshCw, Loader2, X,
  Clock, Filter, Repeat, ChevronLeft, ChevronRight, List, LayoutGrid,
} from "lucide-react";

const CATEGORY_LABELS = {
  shift:      "Shift",
  inspection: "Inspection",
  task:       "Task",
  meeting:    "Meeting",
  deadline:   "Deadline",
  other:      "Other",
};

const STATUS_LABELS = {
  scheduled:   "Scheduled",
  in_progress: "In Progress",
  completed:   "Completed",
  cancelled:   "Cancelled",
};

function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function _daysFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState({ category: "", owner_type: "" });
  // View mode — "agenda" (list grouped by day) or "month" (30-day grid).
  // Persist choice per browser so a dispatcher can pick their default.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("bt_calendar_view") || "agenda"; }
    catch { return "agenda"; }
  });
  // Anchor of the month grid — always the 1st of the currently displayed
  // month at 00:00 UTC. Prev/Next stepping walks by full calendar months.
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    d.setDate(1); d.setHours(0, 0, 0, 0);
    return d;
  });

  useEffect(() => {
    try { localStorage.setItem("bt_calendar_view", viewMode); }
    catch { /* private-mode */ }
  }, [viewMode]);

  // Load window varies with view mode:
  //   agenda → next 60 days (existing)
  //   month  → the visible calendar month + 6-day padding either side
  //            so days that spill into the previous/next month render.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      let fromIso;
      let toIso;
      if (viewMode === "month") {
        const start = new Date(monthAnchor); start.setDate(start.getDate() - 7);
        const end   = new Date(monthAnchor);
        end.setMonth(end.getMonth() + 1); end.setDate(end.getDate() + 7);
        fromIso = start.toISOString();
        toIso   = end.toISOString();
      } else {
        fromIso = new Date().toISOString();
        toIso   = _daysFromNow(60);
      }
      const params = new URLSearchParams({ from: fromIso, to: toIso });
      if (filter.category)   params.set("category", filter.category);
      if (filter.owner_type) params.set("owner_type", filter.owner_type);
      const { data } = await adminApi.get(`/admin/calendar/events?${params.toString()}`);
      setEvents(data?.events || []);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [filter, viewMode, monthAnchor]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/calendar/config");
        setConfig(data);
      } catch (_e) { /* silent */ }
    })();
  }, []);

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const e of events) {
      const day = (e.start_at || "").slice(0, 10);
      if (!buckets.has(day)) buckets.set(day, []);
      buckets.get(day).push(e);
    }
    return Array.from(buckets.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  const removeEvent = async (id, isSeries = false) => {
    let scope = "one";
    if (isSeries) {
      const choice = window.prompt(
        "This event is part of a recurring series.\n" +
        "Type ONE to delete only this occurrence, or SERIES to delete every future occurrence too.",
        "ONE",
      );
      if (!choice) return;
      scope = String(choice).trim().toUpperCase() === "SERIES" ? "series" : "one";
    } else if (!window.confirm("Delete this event?")) {
      return;
    }
    try {
      await adminApi.delete(`/admin/calendar/events/${id}?scope=${scope}`);
      toast.success(scope === "series" ? "Series deleted" : "Event deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-6" data-testid="calendar-page">
      <div className="max-w-6xl mx-auto space-y-4">
        <Link to="/admin"
              className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-400 text-xs font-bold uppercase tracking-widest">
          <ArrowLeft className="h-4 w-4"/>Admin Console
        </Link>

        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 border border-blue-500/40 flex items-center justify-center">
              <CalendarDays className="h-5 w-5 text-blue-400"/>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight">Calendar & Task Scheduler</h1>
              <div className="text-[11px] text-zinc-400 uppercase tracking-widest font-bold">
                Shifts · Inspections · Meetings · Deadlines
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle · agenda ↔ month grid */}
            <div className="flex rounded-lg border border-zinc-700 overflow-hidden"
                 data-testid="calendar-view-toggle">
              <button
                type="button"
                onClick={() => setViewMode("agenda")}
                data-testid="calendar-view-agenda"
                data-active={viewMode === "agenda" ? "1" : "0"}
                className={`h-8 px-2.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest transition ${
                  viewMode === "agenda"
                    ? "bg-blue-500 text-zinc-950"
                    : "bg-zinc-900 text-zinc-300 hover:text-blue-400"
                }`}
              >
                <List className="h-3 w-3"/>Agenda
              </button>
              <button
                type="button"
                onClick={() => setViewMode("month")}
                data-testid="calendar-view-month"
                data-active={viewMode === "month" ? "1" : "0"}
                className={`h-8 px-2.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest transition border-l border-zinc-700 ${
                  viewMode === "month"
                    ? "bg-blue-500 text-zinc-950"
                    : "bg-zinc-900 text-zinc-300 hover:text-blue-400"
                }`}
              >
                <LayoutGrid className="h-3 w-3"/>Month
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
                    data-testid="calendar-reload"
                    className="text-xs border-zinc-700">
              {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3 mr-1"/>}
              Reload
            </Button>
            <Button size="sm" onClick={() => setShowNew(true)}
                    data-testid="calendar-new-event"
                    className="bg-blue-500 hover:bg-blue-400 text-zinc-950 font-bold text-xs">
              <Plus className="h-3 w-3 mr-1"/>New Event
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="calendar-filters">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 font-bold">
            <Filter className="h-3 w-3"/>Filter:
          </span>
          <FilterChip
            active={!filter.category && !filter.owner_type}
            onClick={() => setFilter({ category: "", owner_type: "" })}
            testid="calendar-filter-all"
          >All</FilterChip>
          {config && Object.keys(CATEGORY_LABELS).map((c) => (
            <FilterChip
              key={c}
              active={filter.category === c}
              onClick={() => setFilter((f) => ({ ...f, category: f.category === c ? "" : c }))}
              testid={`calendar-filter-cat-${c}`}
              dot={config?.colors?.[c]}
            >{CATEGORY_LABELS[c]}</FilterChip>
          ))}
          <span className="mx-1 text-zinc-700">·</span>
          {["driver", "staff", "all"].map((t) => (
            <FilterChip
              key={t}
              active={filter.owner_type === t}
              onClick={() => setFilter((f) => ({ ...f, owner_type: f.owner_type === t ? "" : t }))}
              testid={`calendar-filter-owner-${t}`}
            >{t.toUpperCase()}</FilterChip>
          ))}
        </div>

        {/* Event list */}
        {loading && events.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 text-sm italic">
            <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2"/>Loading…
          </div>
        ) : viewMode === "month" ? (
          <MonthGrid
            anchor={monthAnchor}
            setAnchor={setMonthAnchor}
            events={events}
            onEventClick={(e) => removeEvent(e.id, Boolean(e.recurrence_group_id))}
          />
        ) : events.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800 p-10 text-center" data-testid="calendar-empty">
            <CalendarDays className="h-10 w-10 mx-auto mb-3 text-zinc-700"/>
            <div className="text-zinc-400 text-sm">No events in the next 60 days.</div>
            <Button className="mt-4 bg-blue-500 hover:bg-blue-400 text-zinc-950 font-bold" onClick={() => setShowNew(true)}>
              <Plus className="h-4 w-4 mr-1"/>Schedule the first event
            </Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="calendar-agenda-list">
            {grouped.map(([day, dayEvents]) => (
              <div key={day} data-testid={`calendar-day-${day}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5 pl-1">
                  {new Date(day).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
                </div>
                <div className="space-y-1.5">
                  {dayEvents.map((e) => (
                    <EventRow key={e.id} event={e}
                              onDelete={() => removeEvent(e.id, Boolean(e.recurrence_group_id))}/>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {showNew && (
          <NewEventModal
            config={config}
            onClose={() => setShowNew(false)}
            onCreated={() => { setShowNew(false); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function FilterChip({ children, active, onClick, testid, dot }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest transition ${
        active ? "bg-blue-500 text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-blue-500/50"
      }`}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: dot }}/>}
      {children}
    </button>
  );
}

function EventRow({ event, onDelete }) {
  const cat = event.category || "task";
  const isSeries = Boolean(event.recurrence_group_id);
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-3 hover:border-zinc-700 transition-colors"
          data-testid={`calendar-event-row-${event.id}`}
          data-recurring={isSeries ? "1" : "0"}>
      <div className="flex items-center gap-3">
        <div className="h-8 w-1 rounded-full" style={{ backgroundColor: event.color || "#71717a" }}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="font-bold text-sm text-zinc-100 truncate">{event.title}</span>
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest bg-zinc-950 border-zinc-700">
              {CATEGORY_LABELS[cat] || cat}
            </Badge>
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest bg-zinc-950 border-zinc-700 text-zinc-400">
              {STATUS_LABELS[event.status] || event.status}
            </Badge>
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest bg-zinc-950 border-zinc-700 text-zinc-400">
              {event.owner_type}
            </Badge>
            {isSeries && (
              <Badge className="text-[9px] uppercase tracking-widest bg-blue-500/20 border border-blue-500/40 text-blue-300"
                     data-testid={`calendar-event-recurring-badge-${event.id}`}>
                <Repeat className="h-2.5 w-2.5 mr-1"/>Repeats {event.recurrence_freq || ""}
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap">
            <Clock className="h-3 w-3"/>
            {_fmtDate(event.start_at)}
            {event.end_at && ` → ${_fmtDate(event.end_at)}`}
            {event.owner_id && event.owner_type !== "all" && (
              <span className="text-zinc-600 font-mono truncate max-w-[200px]">· {event.owner_id.slice(0, 10)}…</span>
            )}
          </div>
          {event.description && (
            <div className="mt-1.5 text-[11px] text-zinc-500 line-clamp-2">{event.description}</div>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onDelete}
                data-testid={`calendar-event-delete-${event.id}`}
                className="text-rose-400 hover:text-rose-300 h-7 w-7 p-0">
          <Trash2 className="h-3 w-3"/>
        </Button>
      </div>
    </Card>
  );
}

function NewEventModal({ config, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "",
    category: "task", status: "scheduled",
    start_at: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
    end_at: "",
    owner_id: "", owner_type: "staff",
  });
  const [repeat, setRepeat] = useState({
    enabled: false,
    freq: "weekly",     // daily · weekly · monthly
    interval: 1,        // every N units
    count: 8,           // occurrences incl. seed
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.start_at) { toast.error("Start time is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        owner_id: form.owner_id.trim() || null,
      };
      if (repeat.enabled) {
        payload.recurrence = {
          freq:     repeat.freq,
          interval: Math.max(1, Number(repeat.interval) || 1),
          count:    Math.max(1, Number(repeat.count) || 1),
        };
      }
      const { data } = await adminApi.post("/admin/calendar/events", payload);
      const created = data?.occurrences_created;
      if (created && created > 1) {
        toast.success(`Series scheduled — ${created} occurrences`);
      } else {
        toast.success("Event scheduled");
      }
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
         onClick={onClose} data-testid="calendar-new-event-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-zinc-100 flex items-center gap-2">
            <Plus className="h-4 w-4 text-blue-400"/>New Event
          </h3>
          <button type="button" onClick={onClose} data-testid="calendar-new-event-close"
                  className="text-zinc-500 hover:text-zinc-100 p-1">
            <X className="h-4 w-4"/>
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Title *">
            <Input value={form.title} onChange={set("title")}
                   data-testid="calendar-new-event-title"
                   placeholder="e.g. Vehicle inspection · #4321"
                   className="bg-zinc-900 border-zinc-700"/>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onChange={set("category")} testid="calendar-new-event-category">
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={set("status")} testid="calendar-new-event-status">
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start *">
              <Input type="datetime-local" value={form.start_at} onChange={set("start_at")}
                     data-testid="calendar-new-event-start"
                     className="bg-zinc-900 border-zinc-700"/>
            </Field>
            <Field label="End (optional)">
              <Input type="datetime-local" value={form.end_at} onChange={set("end_at")}
                     data-testid="calendar-new-event-end"
                     className="bg-zinc-900 border-zinc-700"/>
            </Field>
          </div>

          {/* ─── Recurrence ─────────────────────────────────────────── */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 space-y-2"
               data-testid="calendar-recurrence-block">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={repeat.enabled}
                onChange={(e) => setRepeat((r) => ({ ...r, enabled: e.target.checked }))}
                data-testid="calendar-repeat-toggle"
                className="h-4 w-4 accent-blue-500"
              />
              <span className="text-xs uppercase tracking-widest font-bold text-zinc-300">
                Repeat this event
              </span>
              {repeat.enabled && (
                <span className="ml-auto text-[10px] font-mono text-blue-400"
                      data-testid="calendar-repeat-preview">
                  {repeat.count}× every {repeat.interval > 1 ? `${repeat.interval} ` : ""}
                  {repeat.freq}
                </span>
              )}
            </label>
            {repeat.enabled && (
              <div className="grid grid-cols-3 gap-2 pt-1">
                <Field label="Frequency">
                  <Select
                    value={repeat.freq}
                    onChange={(e) => setRepeat((r) => ({ ...r, freq: e.target.value }))}
                    testid="calendar-repeat-freq"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </Select>
                </Field>
                <Field label="Every">
                  <Input
                    type="number" min={1} max={52}
                    value={repeat.interval}
                    onChange={(e) => setRepeat((r) => ({ ...r, interval: e.target.value }))}
                    data-testid="calendar-repeat-interval"
                    className="bg-zinc-900 border-zinc-700"
                  />
                </Field>
                <Field label="Occurrences">
                  <Input
                    type="number" min={1} max={260}
                    value={repeat.count}
                    onChange={(e) => setRepeat((r) => ({ ...r, count: e.target.value }))}
                    data-testid="calendar-repeat-count"
                    className="bg-zinc-900 border-zinc-700"
                  />
                </Field>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner Type">
              <Select value={form.owner_type} onChange={set("owner_type")} testid="calendar-new-event-owner-type">
                <option value="staff">Staff (me)</option>
                <option value="driver">Driver</option>
                <option value="all">Org-wide (Level 0)</option>
              </Select>
            </Field>
            <Field label="Owner ID (blank = me)">
              <Input value={form.owner_id} onChange={set("owner_id")}
                     data-testid="calendar-new-event-owner-id"
                     placeholder="driver/staff UUID or leave blank"
                     className="bg-zinc-900 border-zinc-700"/>
            </Field>
          </div>

          <Field label="Description">
            <Textarea rows={2} value={form.description} onChange={set("description")}
                      data-testid="calendar-new-event-description"
                      className="bg-zinc-900 border-zinc-700"/>
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={saving} className="border-zinc-700 text-xs">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}
                  data-testid="calendar-new-event-save"
                  className="bg-blue-500 hover:bg-blue-400 text-zinc-950 font-bold text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : (repeat.enabled ? "Save Series" : "Save Event")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{label}</div>
      {children}
    </label>
  );
}

function Select({ value, onChange, children, testid }) {
  return (
    <select value={value} onChange={onChange} data-testid={testid}
            className="w-full bg-zinc-900 border border-zinc-700 rounded h-10 px-2 text-sm text-zinc-100">
      {children}
    </select>
  );
}


// ─── Month grid view · v2.7.65+E104 ───────────────────────────────────
// Traditional 7-column calendar. Renders the currently-anchored month
// with 6 rows of 7 cells (42 total), spilling into adjacent months for
// the padding cells so the grid is always rectangular. Each cell shows
// the day number and up to 3 event chips coloured by the event's own
// hex color. Overflow indicated by a "+N more" pill.
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABEL_FMT = { month: "long", year: "numeric" };

function _sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

function MonthGrid({ anchor, setAnchor, events, onEventClick }) {
  const today = new Date();

  // Build the 42-cell grid — starting Sunday of the week containing day 1
  // of the anchor month, ending Saturday of the week containing the last day.
  const cells = useMemo(() => {
    const first = new Date(anchor);
    first.setDate(1); first.setHours(0, 0, 0, 0);
    const startOffset = first.getDay(); // 0=Sun
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startOffset);
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push(d);
    }
    return out;
  }, [anchor]);

  // Bucket events by YYYY-MM-DD.
  const eventsByDay = useMemo(() => {
    const buckets = new Map();
    for (const e of events) {
      const day = (e.start_at || "").slice(0, 10);
      if (!day) continue;
      if (!buckets.has(day)) buckets.set(day, []);
      buckets.get(day).push(e);
    }
    // Sort each day by start_at.
    for (const arr of buckets.values()) {
      arr.sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
    }
    return buckets;
  }, [events]);

  const goPrev = () => {
    const d = new Date(anchor); d.setMonth(d.getMonth() - 1); setAnchor(d);
  };
  const goNext = () => {
    const d = new Date(anchor); d.setMonth(d.getMonth() + 1); setAnchor(d);
  };
  const goToday = () => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); setAnchor(d);
  };

  return (
    <div className="space-y-3" data-testid="calendar-month-grid">
      {/* Month nav */}
      <div className="flex items-center justify-between gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2">
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={goPrev}
                  data-testid="calendar-month-prev"
                  className="h-8 w-8 rounded-md border border-zinc-700 text-zinc-300 hover:text-blue-400 hover:border-blue-500/50 flex items-center justify-center">
            <ChevronLeft className="h-4 w-4"/>
          </button>
          <button type="button" onClick={goToday}
                  data-testid="calendar-month-today"
                  className="h-8 px-3 rounded-md border border-zinc-700 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:text-blue-400 hover:border-blue-500/50">
            Today
          </button>
          <button type="button" onClick={goNext}
                  data-testid="calendar-month-next"
                  className="h-8 w-8 rounded-md border border-zinc-700 text-zinc-300 hover:text-blue-400 hover:border-blue-500/50 flex items-center justify-center">
            <ChevronRight className="h-4 w-4"/>
          </button>
        </div>
        <div className="text-sm font-black tracking-wide text-zinc-100"
             data-testid="calendar-month-label">
          {anchor.toLocaleDateString([], MONTH_LABEL_FMT)}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
          {events.length} events
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w}
               className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 text-center py-1">
            {w}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d) => {
          const dayKey = d.toISOString().slice(0, 10);
          const dayEvents = eventsByDay.get(dayKey) || [];
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = _sameDay(d, today);
          return (
            <div
              key={dayKey}
              data-testid={`calendar-month-cell-${dayKey}`}
              data-in-month={inMonth ? "1" : "0"}
              data-today={isToday ? "1" : "0"}
              className={`min-h-[92px] rounded-lg border p-1.5 flex flex-col gap-1 ${
                inMonth
                  ? (isToday
                      ? "bg-blue-500/10 border-blue-500/60"
                      : "bg-zinc-900 border-zinc-800")
                  : "bg-zinc-950 border-zinc-900/70 opacity-60"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-bold ${
                  isToday ? "text-blue-300"
                          : (inMonth ? "text-zinc-200" : "text-zinc-600")
                }`}>
                  {d.getDate()}
                </span>
                {dayEvents.length > 0 && (
                  <span className="text-[9px] font-mono text-zinc-500">
                    {dayEvents.length}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1">
                {dayEvents.slice(0, 3).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onEventClick?.(e)}
                    data-testid={`calendar-month-chip-${e.id}`}
                    title={`${e.title} · ${new Date(e.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
                    className="text-left text-[10px] leading-tight truncate px-1.5 py-0.5 rounded font-semibold hover:brightness-110 transition"
                    style={{
                      backgroundColor: `${e.color || "#71717a"}22`,
                      borderLeft: `3px solid ${e.color || "#71717a"}`,
                      color: e.color || "#a1a1aa",
                    }}
                  >
                    {e.title}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <span className="text-[9px] text-zinc-500 pl-1.5 font-bold"
                        data-testid={`calendar-month-more-${dayKey}`}>
                    +{dayEvents.length - 3} more
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
